import { useCallback } from 'react';

/* eslint-disable import/order -- CI lints `-api` before `/` (correct ASCII), local eslint --fix reverses it; disable to keep CI happy. */
import type { ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { useMutation } from '@atlassian/conversation-assistant-service/common/utils/use-mutation';
/* eslint-enable import/order */

import {
	addConversationToSpaceFetchAction,
	type AddConversationToSpaceRequest,
} from './useAddConversationToSpace.fetch';

type UseAddConversationToSpaceProps = {
	cloudId: string;
	product?: string;
	experienceId?: string;
	onSuccess?: () => void;
	onError?: (error: Error) => void;
};

export const useAddConversationToSpace = ({
	cloudId,
	product = 'rovo',
	experienceId = 'ai-mate',
	onSuccess,
	onError,
}: UseAddConversationToSpaceProps): {
	addConversation: (
		payload: AddConversationToSpaceRequest,
		callbacks?:
			| {
					onSuccess?: ((data: void) => void) | undefined;
					onError?: (error: ApiError) => void;
			  }
			| undefined,
	) => Promise<void | undefined>;
	isSubmitting: boolean;
	error: ApiError | null;
} => {
	const mutationFn = useCallback(
		(request: AddConversationToSpaceRequest) =>
			addConversationToSpaceFetchAction({
				request,
				cloudId,
				product,
				experienceId,
			}),
		[cloudId, product, experienceId],
	);

	const { mutate, isSubmitting, error } = useMutation<AddConversationToSpaceRequest, void>({
		mutationFn,
		onSuccess,
		onError,
	});

	return { addConversation: mutate, isSubmitting, error };
};
